// Hợp đồng API của module `competitors` — Nghiên cứu kênh đối thủ (YouTube Data API v3).
import { z } from 'zod';
/** Số video mới nhất lấy về mỗi lần phân tích / cập nhật. Mỗi 50 video tốn ~2 đơn vị quota. */
export const COMPETITOR_VIDEO_LIMITS = [200, 500, 1000, 2000, 5000];
export const DEFAULT_COMPETITOR_VIDEO_LIMIT = 500;
/**
 * NHÓM của kênh trong danh sách theo dõi (thêm 26/09/2026) — cùng tên với nhóm của bảng «Thị
 * trường», để chuyển kênh sang bên đó là biết ngay xếp vào đâu.
 */
export const COMPETITOR_GROUPS = ['direct', 'indirect', 'market'];
export const COMPETITOR_GROUP_LABELS = {
    direct: 'Đối thủ trực tiếp',
    indirect: 'Đối thủ gián tiếp',
    market: 'Tìm kiếm thị trường',
};
/** Giá trị lạ (dữ liệu cũ) → «đối thủ trực tiếp», đúng như bảng Thị trường xếp kênh chưa phân loại. */
export function readCompetitorGroup(value) {
    return typeof value === 'string' && COMPETITOR_GROUPS.includes(value)
        ? value
        : 'direct';
}
export const competitorGroupRequestSchema = z.object({
    group: z.enum(COMPETITOR_GROUPS),
});
/**
 * HÌNH THỨC MỞ VIDEO (27/09/2026) — đối thủ đưa video lên theo kiểu nào.
 *
 *   • `public`    — video thường, không có dấu vết phát trực tiếp;
 *   • `scheduled` — đã đặt lịch, CHƯA tới giờ (công chiếu hoặc buổi phát trực tiếp sắp diễn ra);
 *   • `live`      — đã hoặc đang phát trực tiếp, gồm cả công chiếu đã chạy xong.
 *
 * SUY RA TỪ `liveStreamingDetails` của YouTube: video thường không có trường ấy, video công chiếu
 * và phát trực tiếp thì có.
 *
 * CHỖ KHÔNG BIẾT ĐƯỢC, nói rõ để khỏi đọc nhầm bảng: một video THƯỜNG được đặt lịch đăng trong
 * Studio, sau khi đã lên thì YouTube trả về y hệt video đăng tay — không còn dấu vết nào. Nên
 * `scheduled` chỉ gặp ở video CHƯA tới giờ; video đã lên rồi thì không truy ngược được.
 *
 * `null` = chưa rõ: dòng lấy về trước 27/09/2026 chưa có thông tin này, phải cập nhật lại kênh mới
 * có. Để trống chứ không đoán thành `public` — đoán thì bảng nói sai mà người đọc không biết.
 */
export const VIDEO_OPEN_MODES = ['public', 'scheduled', 'live'];
export const VIDEO_OPEN_MODE_LABELS = {
    public: 'Công khai',
    scheduled: 'Hẹn lịch',
    live: 'Live',
};
export function readVideoOpenMode(value) {
    return VIDEO_OPEN_MODES.includes(value) ? value : null;
}
export const competitorParamsSchema = z.object({
    id: z.coerce.number().int().positive(),
});
export const addCompetitorRequestSchema = z.object({
    /** Link kênh (`youtube.com/@handle`, `/channel/UC…`, `/c/…`, `/user/…`), link video, `@handle` hay `UC…`. */
    url: z.string().trim().min(1, 'Chưa dán link kênh').max(500),
    maxVideos: z
        .number()
        .int()
        .refine((value) => COMPETITOR_VIDEO_LIMITS.includes(value))
        .optional(),
});
export const refreshCompetitorRequestSchema = z.object({
    maxVideos: addCompetitorRequestSchema.shape.maxVideos,
});
/**
 * VIEW TUẦN của một kênh đang theo dõi, suy ra từ các MỐC số liệu (thêm 26/09/2026) — để màn «Thị
 * trường» khỏi phải gõ tay. Khoá tuần là «DD/MM/YYYY» của ngày Chủ nhật, đúng khoá mà bảng Thị trường
 * đang dùng; giá trị là view TĂNG THÊM trong tuần (âm được — đối thủ xoá video).
 *
 * Tuần nào thiếu mốc ở một trong hai đầu thì KHÔNG có khoá — để trống còn hơn nội suy ra một con số
 * không ai đo được.
 */
/**
 * VIEW THEO TUẦN ĐĂNG của một kênh: cộng view của các video ĐĂNG trong tuần ấy.
 *
 * KHÔNG phải «view cả kênh tăng trong tuần» (`CompetitorWeeklyViewsDto.weeks`) — hai thước đo khác
 * nhau, đừng đổ lẫn vào một cột:
 *
 *   • `weeks`   cần mốc đo ở hai đầu tuần, nên chỉ có từ lúc bắt đầu thu thập trở đi;
 *   • `uploads` suy ra từ video đã tải về, nên CÓ CẢ QUÁ KHỨ — nhưng nó đo sức hút của video MỚI
 *     ĐĂNG, không đo cả kênh, và là view TÍNH ĐẾN HÔM NAY: video đăng 5 tuần trước đã có 5 tuần để
 *     cộng view, video đăng tuần này thì chưa. So kênh với kênh trong CÙNG một tuần thì công bằng;
 *     so tuần này với tuần khác thì tuần cũ luôn được lợi.
 */
/**
 * Mốc đo lệch quá chừng này so với ranh giới tuần thì bỏ, không dùng để tính view tuần.
 *
 * Để ở đây vì CẢ HAI phía cần đúng một con số: máy chủ dùng nó để tính (`weeklyViewDeltas`), trình
 * duyệt dùng nó để nói «mấy tuần đầu không có số». Hai bên lệch nhau thì bảng có số mà lời nhắc bảo
 * là không — đã từng sai đúng như vậy ngày 26/09/2026.
 */
export const WEEK_BOUNDARY_TOLERANCE_MS = 36 * 60 * 60 * 1000;
/**
 * Các tuần cần tính, do trình duyệt gửi lên: `key` là khoá bảng Thị trường («DD/MM/YYYY» của Chủ nhật),
 * `weekStart` là chính ngày ấy lúc 00:00 GIỜ MÁY NGƯỜI DÙNG, dạng ISO — máy chủ không tự đoán múi giờ.
 */
export const competitorWeeklyViewsRequestSchema = z.object({
    weeks: z
        .array(z.object({ key: z.string().min(1).max(20), weekStart: z.string().datetime() }))
        .min(1)
        .max(60),
});
export const addYoutubeKeyRequestSchema = z.object({
    label: z.string().trim().min(1, 'Chưa đặt tên khoá').max(80),
    apiKey: z.string().trim().min(1, 'Chưa dán khoá').max(200),
});
export const youtubeKeyParamsSchema = z.object({
    id: z.coerce.number().int().positive(),
});
// ---------------------------------------------------------------- khoá dùng vào đâu (17/10/2026)
/**
 * Việc tiêu hạn mức khoá (17/10/2026, chủ dự án: «API này tài khoản nào dùng, dùng lượng vào đâu, số lượng»): mỗi lượt
 * gọi Google được ghi sổ theo (khoá, ngày hạn mức, việc, người).
 */
export const YOUTUBE_KEY_FEATURES = {
    'competitor-add': 'Phân tích kênh đối thủ (dán link)',
    'competitor-refresh': 'Cập nhật kênh đối thủ (bấm tay)',
    'competitor-auto': 'Cập nhật kênh đối thủ (tự động rạng sáng)',
    'office-research': 'Phân tích kênh văn phòng',
    'thumbnail-tool': 'Tải thumbnail đối thủ',
    'publish-stats': 'Lượt xem kênh Đăng video (lấy mẫu nền)',
    'video-remake': 'Tải video (tra kênh theo link)',
    'video-info': 'Tải video (thông tin video · tag kênh)',
};
export const researchPermissionsSaveRequestSchema = z.object({
    allowed: z.array(z.string()).max(1000),
});
export const youtubeOauthClientSaveRequestSchema = z.object({
    clientId: z.string().trim().min(1).max(300),
    clientSecret: z.string().trim().min(1).max(300),
});
export const youtubeOauthChannelParamsSchema = z.object({
    youtubeId: z.string().trim().min(1).max(100),
});
/**
 * KHOẢNG THỜI GIAN của bảng chỉ số. Máy chủ lấy sẵn cả bốn mỗi lần «Lấy số» (mỗi khoảng chỉ tốn
 * một lượt hỏi Google), nên đổi khoảng trên giao diện không phải chờ gọi mạng.
 */
export const METRIC_RANGES = ['7d', '30d', '90d', 'all'];
export const METRIC_RANGE_LABELS = {
    // «Tuần này» chứ không phải «7 ngày»: khoảng này là Chủ nhật → Thứ 7, nên sáng Thứ 2 nó mới gom
    // được hai ngày. Ghi «7 ngày» thì con số trông như tụt thảm hại mỗi đầu tuần.
    '7d': 'Tuần này',
    '30d': '30 ngày',
    '90d': '90 ngày',
    all: 'Toàn thời gian',
};
export const channelMetricsQuerySchema = z.object({
    range: z.enum(METRIC_RANGES).optional(),
});
//# sourceMappingURL=competitors.js.map